import type { CivilDate } from '~/models/civilDate'
import { isActiveOn } from '~/models/validPeriod'
import type { PlantUnitAffiliationRow } from '~/pages/AdminPlantUnitMaster/models/unitRows'

/** 所属 BG 列の出し分け。 */
export type AffiliationCell =
  /** 系統コードが無い (所属は系統コードから引くので —)。 */
  | { kind: 'noPlant' }
  /** 系統コードはあるが所属が期間を問わず 1 件も無い (「未設定」の印)。 */
  | { kind: 'unset' }
  | {
      kind: 'memberships'
      /** 出す BG 名 (今日有効な所属、無ければ期間外の所属。同じ BG は 1 つに畳む)。 */
      names: string[]
      /** 今日有効な所属が無く、期間外の所属を出している (低強調)。 */
      outOfPeriod: boolean
      /** 出している所属の「BG コード 開始〜終了」(hover 用)。 */
      periods: string[]
      /** 今日有効な所属に自社の発電 BG が無い (「(管理対象外)」を添える)。 */
      notManaged: boolean
    }

/** 'yyyy/MM/dd' */
export function formatCivilSlash(d: CivilDate): string {
  return `${String(d.year).padStart(4, '0')}/${String(d.month).padStart(2, '0')}/${String(d.day).padStart(2, '0')}`
}

/** 「開始〜終了」 (終了が無ければ「開始〜」)。 */
export function periodText(a: PlantUnitAffiliationRow): string {
  return `${formatCivilSlash(a.validFrom)}〜${a.validTo === undefined ? '' : formatCivilSlash(a.validTo)}`
}

/**
 * 一覧の所属 BG 列の中身を決める。今日有効な所属の BG 名を出し、無ければ期間外の所属を低強調で出す。
 * 今日有効な所属に自社の発電 BG が無ければ「(管理対象外)」を添える (期間外だけの場合も同じ)。
 */
export function affiliationCellOf(
  hasPlantMaster: boolean,
  affiliations: readonly PlantUnitAffiliationRow[],
  today: CivilDate,
): AffiliationCell {
  if (!hasPlantMaster) return { kind: 'noPlant' }
  if (affiliations.length === 0) return { kind: 'unset' }
  const active = affiliations.filter((a) =>
    isActiveOn(a.validFrom, a.validTo, today),
  )
  const shown = active.length > 0 ? active : affiliations
  const names: string[] = []
  const seen = new Set<string>()
  for (const a of shown) {
    if (!seen.has(a.bgMasterId)) {
      seen.add(a.bgMasterId)
      names.push(a.bgName)
    }
  }
  return {
    kind: 'memberships',
    names,
    outOfPeriod: active.length === 0,
    periods: shown.map((a) => `${a.bgCode} ${periodText(a)}`),
    notManaged: !active.some((a) => a.isOwnGenerationBg),
  }
}
